/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Megaphone, Send, Cloud, Shield, Tag, Info, Clock, Users, Plus, X, Edit2, Trash2 } from "lucide-react";
import { useParams } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import {
  createAnnouncement,
  getAnnouncementsForEvent
} from "../../../firebaseFunctions/announcements/announcementService";
import ModalLoadingState from "../../PopUpModal/ModalLoadingState";
import Button from "../../ui/Button";
import { notificationService } from '../../../services/NotificationService';
import { createNotificationsForMany, NOTIFICATION_TYPES } from '../../../services/PebbleNotificationService';

const categoryConfig = {
  general: { icon: Info, color: "bg-blue-100 text-blue-700", labelKey: "eventDetails.generalUpdate" },
  weather: { icon: Cloud, color: "bg-yellow-100 text-yellow-700", labelKey: "eventDetails.weatherAlert" },
  security: { icon: Shield, color: "bg-red-100 text-red-700", labelKey: "eventDetails.securityNotice" },
  promotion: { icon: Tag, color: "bg-green-100 text-green-700", labelKey: "eventDetails.promotion" },
};

const formatDate = (date) => {
  if (!date) return "";
  const dateObj = date instanceof Date ? date : new Date(date);
  const now = new Date();
  const diffMs = now - dateObj;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return dateObj.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: dateObj.getFullYear() !== now.getFullYear() ? "numeric" : undefined
  });
};

const AnnouncementCard = ({ announcement }) => {
  const { t } = useTranslation();
  const category = categoryConfig[announcement.category] || categoryConfig.general;
  const CategoryIcon = category.icon;

  return (
    <div className="p-4 border border-grey-200 rounded-xl bg-white hover:border-grey-300 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${category.color}`}>
              <CategoryIcon className="h-3 w-3" />
              {t(category.labelKey)}
            </span>
            <span className="flex items-center gap-1 text-xs text-grey-500">
              <Clock className="h-3 w-3" />
              {formatDate(announcement.createdAt)}
            </span>
          </div>
          <h4 className="font-semibold text-grey-900 mb-1">{announcement.title}</h4>
          <p className="text-sm text-grey-600">{announcement.message}</p>
        </div>
        <div className="flex items-center gap-1 text-xs text-grey-500 shrink-0">
          <Users className="h-3.5 w-3.5" />
          <span>{announcement.readBy?.length || 0} {t('eventDetails.readCount')}</span>
        </div>
      </div>
    </div>
  );
};

const AnnouncementComposer = ({ eventId, organizerId, onClose, onSuccess, vendors = [], eventName = '' }) => {
  const { t } = useTranslation();
  const categories = [
    { id: "general", label: t('eventDetails.generalUpdate'), icon: Info, color: "bg-blue-100 text-blue-700" },
    { id: "weather", label: t('eventDetails.weatherAlert'), icon: Cloud, color: "bg-yellow-100 text-yellow-700" },
    { id: "security", label: t('eventDetails.securityNotice'), icon: Shield, color: "bg-red-100 text-red-700" },
    { id: "promotion", label: t('eventDetails.promotion'), icon: Tag, color: "bg-green-100 text-green-700" },
  ];
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("general");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState("");
  const [recipientMode, setRecipientMode] = useState('all'); // 'all' | 'approved' | 'selected'
  const [selectedVendorIds, setSelectedVendorIds] = useState([]);
  const [isSendingSMS, setIsSendingSMS] = useState(false);
  const [sendSMS, setSendSMS] = useState(true); // checkbox to also send SMS

  const approvedVendors = vendors.filter(v =>
    v.applicationStatus === 'Approved' || v.applicationStatus === 'Registered'
  );
  const targetVendors = recipientMode === 'all'
    ? vendors
    : recipientMode === 'approved'
    ? approvedVendors
    : vendors.filter(v => selectedVendorIds.includes(v.id || v.userId));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!title.trim() || !message.trim()) {
      setError(t('eventDetails.fillInTitleAndMessage'));
      return;
    }

    setIsSending(true);

    try {
      const result = await createAnnouncement({
        eventId,
        organizerId,
        title: title.trim(),
        message: message.trim(),
        category,
        targetVendorIds: recipientMode === 'selected'
          ? selectedVendorIds
          : recipientMode === 'approved'
          ? approvedVendors.map(v => v.id || v.userId)
          : [],
      });

      if (result.success) {
        // Fire in-app notifications for each targeted vendor
        const recipientIds = targetVendors.map(v => v.id || v.userId || v.mpsUserId).filter(Boolean);
        if (recipientIds.length > 0) {
          createNotificationsForMany(recipientIds, {
            recipientType: 'mps',
            type: NOTIFICATION_TYPES.ANNOUNCEMENT,
            title: title.trim(),
            message: `${eventName}: ${message.trim().slice(0, 120)}${message.trim().length > 120 ? '…' : ''}`,
            eventId,
            linkPath: `/mps/view-event-details/${eventId}?tab=announcements`,
          }).catch(e => console.error('Announcement notification error', e));
        }

        if (sendSMS && targetVendors.length > 0) {
          const smsRecipients = targetVendors
            .filter(v => v.businessPhoneNumber)
            .map(v => ({ phoneNumber: v.businessPhoneNumber, name: v.businessName }));
          if (smsRecipients.length > 0) {
            notificationService.sendAnnouncementSMS({
              recipients: smsRecipients,
              message: message.trim(),
              eventName,
              senderName: '',
            }).catch(e => console.error('SMS announcement error', e));
          }
        }
        onSuccess?.();
        onClose?.();
      } else {
        setError(result.error || t('eventDetails.failedToSend'));
      }
    } catch (err) {
      setError(t('eventDetails.sendError'));
      console.error("Announcement error:", err);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-grey-200 shadow-sm overflow-hidden mb-6">
      <div className="px-5 py-4 border-b border-grey-200 bg-grey-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-grey-600" />
          <h3 className="font-semibold text-grey-900">{t('eventDetails.newAnnouncement')}</h3>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1 hover:bg-grey-200 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-grey-500" />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="p-5 space-y-4">
        <div>
          <label className="block text-sm font-medium text-grey-700 mb-2">
            {t('eventDetails.category')}
          </label>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setCategory(cat.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  category === cat.id
                    ? cat.color + " ring-2 ring-offset-1 ring-grey-400"
                    : "bg-grey-100 text-grey-600 hover:bg-grey-200"
                }`}
              >
                <cat.icon className="h-3.5 w-3.5" />
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Recipient picker */}
        {vendors.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-grey-700 mb-2">
              Send to
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {[
                { id: 'all', label: `All vendors (${vendors.length})` },
                { id: 'approved', label: `Approved only (${approvedVendors.length})` },
                { id: 'selected', label: 'Select specific' },
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => { setRecipientMode(opt.id); setSelectedVendorIds([]); }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    recipientMode === opt.id
                      ? 'bg-grey-900 text-white'
                      : 'bg-grey-100 text-grey-600 hover:bg-grey-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {recipientMode === 'selected' && (
              <div className="border border-grey-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                {vendors.map(v => {
                  const vid = v.id || v.userId;
                  const isSelected = selectedVendorIds.includes(vid);
                  return (
                    <button
                      key={vid}
                      type="button"
                      onClick={() => setSelectedVendorIds(prev =>
                        isSelected ? prev.filter(id => id !== vid) : [...prev, vid]
                      )}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors border-b border-grey-100 last:border-0 ${
                        isSelected ? 'bg-grey-50' : 'hover:bg-grey-50'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded flex items-center justify-center border flex-shrink-0 ${
                        isSelected ? 'bg-grey-900 border-grey-900' : 'border-grey-300'
                      }`}>
                        {isSelected && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 12"><path d="M10 3L5 8.5 2 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>}
                      </div>
                      <span className="font-medium text-grey-900">{v.businessName}</span>
                      <span className="text-grey-400 ml-auto text-xs">{v.applicationStatus}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div>
          <label htmlFor="title" className="block text-sm font-medium text-grey-700 mb-2">
            {t('eventDetails.announcementTitle')}
          </label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Important Weather Update"
            className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            maxLength={100}
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-grey-700 mb-2">
            {t('eventDetails.announcementMessage')}
          </label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter your announcement message here..."
            rows={4}
            className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            maxLength={500}
          />
          <p className="mt-1 text-xs text-grey-500 text-right">
            {message.length}/500 characters
          </p>
        </div>

        {targetVendors.some(v => v.businessPhoneNumber) && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sendSMS}
              onChange={e => setSendSMS(e.target.checked)}
              className="w-4 h-4 rounded border-grey-300"
            />
            <span className="text-sm text-grey-700">
              Also send SMS to vendors with phone numbers ({targetVendors.filter(v => v.businessPhoneNumber).length})
            </span>
          </label>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-grey-500">
            Sending to {targetVendors.length} vendor{targetVendors.length !== 1 ? 's' : ''}
            {sendSMS && targetVendors.filter(v => v.businessPhoneNumber).length > 0
              ? ` + SMS to ${targetVendors.filter(v => v.businessPhoneNumber).length}`
              : ''}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="transparent"
              onClick={onClose}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSending || !title.trim() || !message.trim()}
            >
              {isSending ? (
                <span className="flex items-center gap-2">
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t('eventDetails.sending')}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  {t('eventDetails.sendAnnouncement')}
                </span>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

const Announcements = ({ userData, mpsRegistrations = [], eventName = '' }) => {
  const { t } = useTranslation();
  const { id: eventId } = useParams();
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);

  const loadAnnouncements = async () => {
    if (!eventId) return;

    setIsLoading(true);
    try {
      const result = await getAnnouncementsForEvent(eventId);
      if (result.success) {
        setAnnouncements(result.announcements);
      }
    } catch (error) {
      console.error("Error loading announcements:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAnnouncements();
  }, [eventId]);

  const handleAnnouncementSuccess = () => {
    setShowComposer(false);
    loadAnnouncements();
  };

  if (isLoading) {
    return <ModalLoadingState />;
  }

  return (
    <div className="w-full p-6">
      <div className="flex items-center justify-between mb-6 pl-1 pr-1 sm:pr-8 sm:pl-8">
        <div className="flex items-center gap-3">
          <Megaphone className="h-6 w-6 text-grey-700" />
          <h2 className="text-xl font-medium text-grey-900">{t('eventDetails.announcements')}</h2>
          {announcements.length > 0 && (
            <span className="text-sm text-grey-500">({announcements.length})</span>
          )}
        </div>
        {!showComposer && (
          <Button
            onClick={() => setShowComposer(true)}
            className="flex items-center gap-1.5"
          >
            <Plus className="h-4 w-4" />
            {t('eventDetails.newAnnouncement')}
          </Button>
        )}
      </div>

      <div className="pl-1 pr-1 sm:pr-8 sm:pl-8">
        {showComposer && (
          <AnnouncementComposer
            eventId={eventId}
            organizerId={user?.uid}
            onClose={() => setShowComposer(false)}
            onSuccess={handleAnnouncementSuccess}
            vendors={mpsRegistrations}
            eventName={eventName}
          />
        )}

        {announcements.length === 0 ? (
          <div className="text-center py-16 bg-grey-50 rounded-xl border border-grey-200">
            <div className="mx-auto w-16 h-16 bg-grey-100 rounded-full flex items-center justify-center mb-4">
              <Megaphone className="h-8 w-8 text-grey-400" />
            </div>
            <h3 className="text-lg font-medium text-grey-900 mb-2">{t('eventDetails.noAnnouncementsYet')}</h3>
            <p className="text-grey-500 max-w-md mx-auto mb-6">
              {t('eventDetails.noAnnouncementsDesc')}
            </p>
            {!showComposer && (
              <Button
                onClick={() => setShowComposer(true)}
                className="inline-flex items-center gap-1.5"
              >
                <Plus className="h-4 w-4" />
                {t('eventDetails.createFirstAnnouncement')}
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {announcements.map((announcement) => (
              <AnnouncementCard key={announcement.id} announcement={announcement} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Announcements;
